import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import DeleteModalButton from "../../../../components/DeleteModalBtn/DeleteModalButton";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import { truncateString } from "utils/UtilityFuncs";
import WRateTypes from "./WRateTypes";

export default function WRates() {
  const dispatch = useDispatch();

  // Core state
  const [rates, setRates] = useState([]);
  const [departments, setDepartments] = useState([]);

  const [filteredDepartments, setFilteredDepartments] = useState(null);

  const [styles, setStyles] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedStyleId, setSelectedStyleId] = useState("");
  const [styleSearch, setStyleSearch] = useState("");
  const [rateTypeFilter, setRateTypeFilter] = useState("none");

  // Form / modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [loading, setLoading] = useState(false);

  // Rate type selection state (list comes from child component)
  const [rateTypes, setRateTypes] = useState([]); // keep for select dropdown only
  const [selectedRateType, setSelectedRateType] = useState("");

  // Tabs
  const [activeTab, setActiveTab] = useState("rates");

  // Derived maps
  const deptById = useMemo(() => {
    const map = new Map();
    departments?.forEach((d) => map.set(d?.id, d));
    return map;
  }, [departments]);

  const styleByStyleId = useMemo(() => {
    const map = new Map();
    styles?.forEach((s) => map.set(s?.styleId, s));
    return map;
  }, [styles]);

  const allRatesForSelectedStyle = useMemo(() => {
    if (!selectedStyleId) return [];
    const selectedProductId = selectedProduct?.productId;
    if (!selectedProductId) return [];
    return (rates || [])
      .filter((r) => Number(r?.productId) === Number(selectedProductId))
      .sort((a, b) => (a?.sortId || 0) - (b?.sortId || 0));
  }, [rates, selectedStyleId, styleByStyleId, selectedProduct]);

  const ratesForSelectedStyle = useMemo(() => {
    if (rateTypeFilter === "none") return allRatesForSelectedStyle;
    return allRatesForSelectedStyle.filter(
      (r) => r?.rateType === rateTypeFilter
    );
  }, [allRatesForSelectedStyle, rateTypeFilter]);

  const totalCost = useMemo(
    () =>
      ratesForSelectedStyle.reduce((sum, r) => sum + (Number(r?.rate) || 0), 0),
    [ratesForSelectedStyle]
  );

  const selectedStyleObj = useMemo(
    () => (styles || []).find((s) => s?.styleId === selectedStyleId) || null,
    [styles, selectedStyleId]
  );

  // --- API ---
  const getDepartments = useCallback(async () => {
    try {
      const { data } = await API.get("/manufacturer/wages/departments", {
        headers: authHeader(),
      });
      if (data?.success) setDepartments(data?.data || []);
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    }
  }, [dispatch]);

  const getStyles = async () => {
    try {
      const { data } = await API.post(
        "/manufacturer/products/with-rates",
        { page: 1, limit: 200, filterBy: "none" },
        { headers: authHeader() }
      );
      if (data?.success) setStyles(data?.data || []);
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    }
  };

  const getRates = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await API.post(
        "/manufacturer/wages/rate/search",
        {
          styleId: selectedStyleId,
        },
        {
          headers: authHeader(),
        }
      );
      if (data?.success) setRates(data?.data || []);
      else dispatch(setNotification({ message: data?.message, type: "error" }));
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  }, [dispatch, selectedStyleId]);

  const addRate = async (e) => {
    e.preventDefault();
    try {
      if (!selectedStyleId || !selectedProduct) {
        dispatch(
          setNotification({
            message: "Select a valid style first",
            type: "error",
          })
        );
        return;
      }
      setLoading(true);

      const payload = {
        ...formData,
        styleId: selectedStyleId,
        productId: selectedProduct?.productId,
        rate: formData?.rate === "" ? 0 : Number(formData?.rate),
      };
      const { data } = await API.post("/manufacturer/wages/rate", payload, {
        headers: authHeader(),
      });
      if (data?.success) {
        setRates((prev) => [...prev, data?.data]);
        await getStyles();
        setFormData({});
        setShowAddModal(false);
      } else {
        dispatch(setNotification({ message: data?.message, type: "error" }));
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  const updateRate = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const resolvedStyleId = String(
        formData?.styleId ?? selectedStyleId ?? ""
      );
      const resolvedProductId = Number(
        formData?.productId ??
          styleByStyleId.get(formData?.styleId ?? selectedStyleId)?.id
      );
      const payload = {
        ...formData,
        styleId: resolvedStyleId,
        productId: resolvedProductId,
        rate: formData?.rate === "" ? 0 : Number(formData?.rate),
      };
      const { data } = await API.patch("/manufacturer/wages/rate", payload, {
        headers: authHeader(),
      });
      if (data?.success) {
        setRates((prev) =>
          prev.map((r) => (r.id === formData.id ? data?.data : r))
        );
        setIsUpdateMode(false);
        setShowAddModal(false);
      } else {
        dispatch(setNotification({ message: data?.message, type: "error" }));
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  const deleteRate = async (id) => {
    try {
      setLoading(true);
      const { data } = await API.delete(`/manufacturer/wages/rate/${id}`, {
        headers: authHeader(),
      });
      if (data?.success) {
        setRates((prev) => prev.filter((r) => r.id !== id));
        await getStyles();
        dispatch(
          setNotification({ message: "Deleted successfully", type: "success" })
        );
      } else {
        dispatch(
          setNotification({
            message: data?.message || "Failed to delete",
            type: "error",
          })
        );
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    } finally {
      setLoading(false);
    }
  };

  // Sorting Up
  const sortUp = async (id) => {
    const index = ratesForSelectedStyle.findIndex((item) => item.id === id);
    if (index === -1) return;
    if (index === 0) return;
    if (index === ratesForSelectedStyle.length) return;

    const currentSortId = ratesForSelectedStyle[index]?.sortId;
    const currentId = ratesForSelectedStyle[index]?.id;
    const upSortId = ratesForSelectedStyle[index - 1].sortId;
    const upId = ratesForSelectedStyle[index - 1].id;
    try {
      const { data } = await API.post(
        "manufacturer/wages/rate/sort-up",
        { currentSortId, currentId, upSortId, upId },
        { headers: authHeader() }
      );
      if (data.success) {
        await getRates();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  // Sorting Up
  const sortDown = async (id) => {
    const index = ratesForSelectedStyle.findIndex((item) => item.id === id);
    if (index === -1) return;
    if (index === ratesForSelectedStyle.length - 1) return;

    const currentSortId = ratesForSelectedStyle[index]?.sortId;
    const currentId = ratesForSelectedStyle[index]?.id;
    const downSortId = ratesForSelectedStyle[index + 1].sortId;
    const downId = ratesForSelectedStyle[index + 1].id;

    try {
      const { data } = await API.post(
        "manufacturer/wages/rate/sort-down",
        { currentSortId, currentId, downSortId, downId },
        { headers: authHeader() }
      );
      if (data.success) {
        await getRates();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const getRateTypes = async () => {
    try {
      const { data } = await API.post(
        "/rate-types",
        { styleId: selectedStyleId },
        { headers: authHeader() }
      );
      if (data?.success) {
        setRateTypes(data?.data || []);
        setSelectedRateType(data?.data[0]?.name || "");
        setRateTypeFilter(data?.data[0]?.name || "");
      }
    } catch (err) {
      dispatch(setNotification({ message: err.message, type: "error" }));
    }
  };

  // Handlers
  const handleUpdateData = (item) => {
    const pid = item?.productId ?? styleByStyleId.get(item?.styleId)?.id;
    const styleObj = styles?.find((s) => Number(s?.id) === Number(pid));
    setFormData({
      ...item,
      productId: pid,
      styleId: item?.styleId ?? styleObj?.styleId,
    });
    setIsUpdateMode(true);
    setShowAddModal(true);
  };

  const handleRateTypeSelect = (name) => {
    setSelectedRateType(name);
    setRateTypeFilter(name || "none");
  };

  useEffect(() => {
    let t = setTimeout(() => {
      getDepartments();
      getStyles();
    }, 50);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    let timeout = setTimeout(() => {
      getRates();
    }, 50);
    return () => clearTimeout(timeout);
  }, [selectedStyleId]);

  useEffect(() => {
    if (!selectedStyleId && styles?.length) {
      setSelectedStyleId(styles[0]?.styleId || "");
      setSelectedProduct(styles[0]);
    }
  }, [styles, selectedStyleId]);

  const formatDT = (s) => {
    if (!s) return "-";
    try {
      const d = new Date(s);
      if (Number.isNaN(d.getTime())) return String(s);
      return d.toLocaleString();
    } catch {
      return String(s);
    }
  };

  useEffect(() => {
    // setRateTypeFilter("none");
    // setRateTypes([]);
    let timeout = setTimeout(() => {
      if (selectedStyleId) {
        getRateTypes();
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [selectedStyleId]);

  useEffect(() => {
    if (rates && departments && selectedRateType) {
      let filteredRates = rates.filter(
        (rate) => rate.rateType === selectedRateType
      );
      let existingDepartmentIds = new Set(
        filteredRates.map((rate) => rate.departmentId)
      );
      setFilteredDepartments(
        departments.filter((dept) => !existingDepartmentIds.has(dept.id))
      );
    }
  }, [departments, rates, selectedStyleId, selectedRateType]);

  return (
    <div className="p-2 bg-light" style={{ height: "79vh" }}>
      {loading && <Spinner />}
      <div className="d-flex gap-2 h-100">
        {/* Left Styles */}
        <div
          className="bg-white rounded custom-shadow-sm p-2"
          style={{ height: "100%", overflow: "hidden", flex: "0 0 50%" }}
        >
          <div className="d-flex align-items-center gap-2 mb-2">
            <input
              value={styleSearch}
              onChange={(e) => setStyleSearch(e.target.value)}
              className="form-control form-control-sm"
              placeholder="Search style ID or description"
              style={{ maxWidth: 320 }}
            />
            <button
              className="btn btn-danger btn-sm"
              onClick={() => setStyleSearch("")}
            >
              Clear
            </button>
          </div>
          <div style={{ height: "100%", overflowY: "auto" }}>
            <table className="table table-sm table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "30%" }}>Style Number</th>
                  <th style={{ width: "30%" }}>Description</th>
                  <th>Update Time</th>
                  <th>No of rates</th>
                </tr>
              </thead>
              <tbody>
                {styles?.map((s) => {
                  const active = s?.styleId === selectedStyleId;
                  return (
                    <tr
                      key={s?.id}
                      onClick={() => {
                        setSelectedStyleId(s?.styleId);
                        setSelectedProduct(s);
                        setSelectedRateType("");
                        setRateTypeFilter("none");
                      }}
                      style={{ cursor: "pointer" }}
                      className={active ? "table-primary" : ""}
                    >
                      <td>{s?.styleId}</td>
                      <td>{truncateString(s?.desc, 20)}</td>
                      <td>{formatDT(s?.updatedAt || s?.updated_at)}</td>
                      <td>{s?.m_rateTypes?.length || 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right Tabs */}
        {selectedStyleId && (
          <div
            className="bg-white rounded custom-shadow-sm p-2 d-flex flex-column"
            style={{ height: "100%", overflow: "hidden", flex: "1 1 auto" }}
          >
            <ul className="nav nav-pills mb-3">
              <li className="nav-item custom-shadow-sm">
                <button
                  type="button"
                  className={
                    "nav-link " +
                    (activeTab === "rates" ? "active" : "bg-light")
                  }
                  onClick={() => setActiveTab("rates")}
                >
                  Rates
                </button>
              </li>
              <li className="nav-item custom-shadow-sm">
                <button
                  type="button"
                  className={
                    "nav-link " +
                    (activeTab === "rateTypes" ? "active" : "bg-light")
                  }
                  onClick={() => setActiveTab("rateTypes")}
                >
                  Rate Type
                </button>
              </li>
            </ul>

            {activeTab === "rates" ? (
              <>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <div className="fw-bold text-muted fs-5">
                      Selected Style:
                    </div>
                    <div className="badge fs-5 bg-primary bg-opacity-10 text-primary border border-primary">
                      {selectedStyleId || "None"}
                    </div>
                    {selectedStyleObj?.desc && (
                      <span
                        className="text-muted fs-5 text-truncate"
                        style={{ maxWidth: 380 }}
                      >
                        {selectedStyleObj.desc}
                      </span>
                    )}
                  </div>
                  {rateTypes && rateTypes.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={!selectedStyleId}
                      onClick={() => {
                        setIsUpdateMode(false);
                        const pid = styleByStyleId.get(selectedStyleId)?.id;
                        setFormData({
                          styleId: selectedStyleId,
                          productId: pid,
                          departmentId: "",
                          rate: "",
                          rateType:
                            rateTypeFilter !== "none" ? rateTypeFilter : "",
                        });
                        setShowAddModal(true);
                      }}
                    >
                      Add Rate
                    </button>
                  )}
                </div>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <label className="small text-muted mb-0">Rate Type</label>
                  <select
                    className="form-select form-select-sm"
                    style={{ maxWidth: 240 }}
                    value={rateTypeFilter}
                    onChange={(e) => {
                      const v = e.target.value;
                      setRateTypeFilter(v);
                      setSelectedRateType(v === "none" ? "" : v);
                    }}
                  >
                    <option disabled value="none">
                      None
                    </option>
                    {(rateTypes || []).map((rt) => {
                      const rtName = rt?.name ?? rt?.rateType ?? rt;
                      return (
                        <option key={rt?.id ?? rtName} value={rtName}>
                          {rtName}
                        </option>
                      );
                    })}
                  </select>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setActiveTab("rateTypes")}
                  >
                    Manage Types
                  </button>
                </div>
                <div style={{ height: "100%", overflowY: "auto" }}>
                  <table className="table table-sm align-middle">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "20%" }}>Department</th>
                        <th style={{ width: "20%" }}>Rate</th>
                        <th style={{ width: "20%" }}>Rate Type</th>
                        <th style={{ width: "40%" }} className="text-end">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {ratesForSelectedStyle.map((item) => {
                        const dept = deptById.get(item?.departmentId);
                        const list = allRatesForSelectedStyle;
                        const idx = list.findIndex((r) => r.id === item?.id);
                        const isFirst = idx === 0;
                        const isLast = idx === list.length - 1;
                        return (
                          <tr key={item?.id}>
                            <td>
                              {dept?.departmentName ||
                                item?.departmentName ||
                                item?.departmentId}
                            </td>
                            <td>{item?.rate}</td>
                            <td>{item?.rateType || "N/A"}</td>
                            <td className="text-end">
                              <button
                                title="Move up"
                                onClick={() => sortUp(item?.id)}
                                disabled={loading || isFirst}
                                className="btn btn-sm btn-outline-secondary me-1"
                              >
                                <i className="mdi mdi-chevron-double-up"></i>
                              </button>
                              <button
                                title="Move down"
                                onClick={() => sortDown(item?.id)}
                                disabled={loading || isLast}
                                className="btn btn-sm btn-outline-secondary me-1"
                              >
                                <i className="mdi mdi-chevron-double-down"></i>
                              </button>
                              <button
                                disabled={loading}
                                onClick={() => handleUpdateData(item)}
                                className="btn btn-sm btn-outline-secondary me-1"
                              >
                                <i className="mdi mdi-pencil"></i>
                              </button>
                              <DeleteModalButton
                                btnClass={"btn btn-sm btn-outline-danger"}
                                deleteFunc={deleteRate}
                                deleteParams={item.id}
                                disableConditions={loading}
                              />
                            </td>
                          </tr>
                        );
                      })}
                      {ratesForSelectedStyle.length === 0 && (
                        <tr>
                          <td
                            colSpan={4}
                            className="text-center text-muted py-4"
                          >
                            {rateTypes && rateTypes.length > 0
                              ? selectedStyleId
                                ? "No rates found for this style."
                                : "Select a style to view rates."
                              : "No rate types available. Please add a rate type."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                    {rateTypeFilter && rateTypeFilter !== "none" && (
                      <tfoot>
                        <tr>
                          <td colSpan={4} className="text-end fw-semibold">
                            Total Cost: {Number(totalCost).toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </>
            ) : (
              <WRateTypes
                selectedRateType={selectedRateType}
                onSelect={handleRateTypeSelect}
                selectedStyleId={selectedStyleId}
                _setRateTypes={setRateTypes}
                refreshFunc={getStyles}
              />
            )}
          </div>
        )}
      </div>

      {/* Add/Update Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Body className=" bg-white px-3" style={{ borderRadius: 15 }}>
          <div className="">
            <h4>{isUpdateMode ? "Update Wage Rate" : "Add Wage Rate"}</h4>
          </div>
          <hr className="mt-0" />
          <form onSubmit={isUpdateMode ? updateRate : addRate}>
            <div className="mb-2">
              <label className="form-label">Style</label>
              <input
                className="form-control border-primary bg-primary bg-opacity-10"
                disabled
                readOnly
                value={
                  formData?.styleId
                    ? `${formData.styleId}${
                        styleByStyleId.get(formData.styleId)?.desc
                          ? ` - ${styleByStyleId.get(formData.styleId)?.desc}`
                          : ""
                      }`
                    : ""
                }
              />
            </div>
            <div className="mb-2">
              <label className="form-label">Department</label>
              <select
                required
                className="form-select"
                value={formData?.departmentId || ""}
                disabled={!formData?.productId}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    departmentId: Number(e.target.value),
                  }))
                }
              >
                <option value="" disabled>
                  Select department
                </option>
                {(isUpdateMode ? departments : filteredDepartments || []).map(
                  (d) => (
                    <option key={d?.id} value={d?.id}>
                      {d?.departmentName}
                    </option>
                  )
                )}
              </select>
            </div>
            <div className="mb-2">
              <label className="form-label">Rate</label>
              <input
                type="number"
                step="0.01"
                min="0"
                required
                className="form-control"
                placeholder="Enter rate"
                value={formData?.rate ?? ""}
                disabled={!formData?.productId}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    rate: e.target.value === "" ? "" : Number(e.target.value),
                  }))
                }
              />
            </div>
            <div className="mt-3 d-flex justify-content-end">
              <button
                onClick={() => setShowAddModal(false)}
                className="btn btn-secondary btn-sm"
                type="button"
              >
                Close
              </button>
              <button
                disabled={
                  loading ||
                  !formData?.productId ||
                  !formData?.departmentId ||
                  formData?.rate === undefined ||
                  formData?.rate === ""
                }
                className="btn btn-primary btn-sm ms-1"
                type="submit"
              >
                {isUpdateMode ? "Update" : "Add"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}
